import { useState } from 'react'
import dayjs from 'dayjs'

import { useBestPerformingPlatformPosts } from 'hooks/queries/useBestPerformingPlatformPosts'
import { useRefreshBestPerformingPosts } from 'hooks/queries/useRefreshBestPerformingPosts'
import { useBestPerformingPostsChannel } from 'components/Wizards/RecommendMoreContentWizard/hooks/useBestPerformingPostsChannel'
import useURLParams from 'hooks/useURLParams'
import { useModals } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

import Styles from './BestPerformingPlatformPosts.module.scss'

import { Section } from '../components/Section/Section'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button/Button'
import { RecommendMoreContentWizard } from 'components/Wizards/RecommendMoreContentWizard/RecommendMoreContentWizard'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import { DeclarativeToast } from 'common/ToastMessage/DeclarativeToast'
import { ToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Carousel } from 'components/Carousel/Carousel'
import { PostPreview } from './components/PostPreview/PostPreview'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useInitiateOauth } from 'hooks/useInitiateOauth'

export const Post = ({ post, externalIntegrationId }: { post: any; externalIntegrationId: number }) => {
  const { openModal } = useModals()

  const recommentMoreContent = () => {
    openModal(RecommendMoreContentWizard, { postId: post.id, topic: post.topic, externalIntegrationId })
  }

  return (
    <div className={Styles.post}>
      <div className={Styles.preview}>
        <PostPreview post={post} externalIntegrationId={externalIntegrationId} />
      </div>

      <div className={Styles.data}>
        <div className={Styles.engagement}>
          <Text color="var(--dark-60)" variant="secondary">
            Engagement
          </Text>
          <Heading level={4}>{Number(post.engagement_rate).toFixed(1)}%</Heading>
        </div>
        <div className={Styles.more}>
          <div className={Styles.itemsContainer}>
            <div className={Styles.item}>
              <Text variant="secondary">Likes</Text>
              <Text variant="smallList">{post.like_count}</Text>
            </div>
            <div className={Styles.item}>
              <Text variant="secondary">Comments</Text>
              <Text variant="smallList">{post.comments_count}</Text>
            </div>
          </div>
          <div className={Styles.footer}>
            <Button variant="ghost" size="sm" onPress={() => recommentMoreContent()} fullWidth>
              <div className={Styles.postButton}>
                <span>Create more</span>
                <span className={Styles.nonBreakingSpace}>&nbsp;</span>
                <span>content like this</span>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const BestPerformingPlatformPosts = () => {
  const [searchParams] = useURLParams()
  const externalIntegrationId = parseInt(searchParams.get('external_integration_id') as string)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { data, refetch, isRefetching, isLoading } = useBestPerformingPlatformPosts({ externalIntegrationId })
  const { mutate: refreshPosts } = useRefreshBestPerformingPosts({
    externalIntegrationId,
  })
  const { isMobile } = useResponsiveDesign()
  useBestPerformingPostsChannel({ externalIntegrationId, refetch, setIsRefreshing })
  const externalIntegrations = useExternalIntegrations().data?.external_integrations
  const externalIntegration = externalIntegrations?.find((i) => i.id === externalIntegrationId)
  const { initiateOauth } = useInitiateOauth()

  const refresh = () => {
    setIsRefreshing(true)
    refreshPosts()
  }

  const refreshToken = () => {
    if (!externalIntegration) return

    const { provider, id } = externalIntegration
    initiateOauth(provider, id as number)
  }

  const created_at = data?.posts?.[0]?.created_at
  const hasPosts = !isLoading && (data?.posts?.length || 0) > 0
  const hasInvalidFacebookToken =
    externalIntegration?.provider === 'facebook' && !externalIntegration?.scopes.includes('pages_read_user_content')
  const actions = hasPosts ? (
    <Button variant="secondary" frontIcon={ArrowRefresh2} onPress={refresh}>
      {!isMobile && 'Refresh'}
    </Button>
  ) : null

  return (
    <Section
      title={`Best Performing Posts${
        created_at ? ` as of ${dayjs(created_at).format('M')}/${dayjs(created_at).format('D')}` : ''
      }`}
      description="Quickly see which 5 posts have the best engagement from the last 30 posts"
      actions={actions}
    >
      {hasPosts && (
        <div className={Styles.container}>
          {(isRefreshing || isRefetching) && (
            <>
              <div className={Styles.loadingOverlay} />
              <DeclarativeToast>
                <ToastMessage toastMessage="Fetching best performing posts..." variant="generating" />
              </DeclarativeToast>
            </>
          )}
          {isMobile ? (
            <Carousel offset={0} withControls={false}>
              {data?.posts.slice(0, 5).map((post) => (
                <Post key={post.id} post={post} externalIntegrationId={externalIntegrationId} />
              ))}
            </Carousel>
          ) : (
            <>
              {data?.posts.slice(0, 5).map((post) => (
                <Post key={post.id} post={post} externalIntegrationId={externalIntegrationId} />
              ))}
            </>
          )}
        </div>
      )}
      {!isLoading && !hasPosts && hasInvalidFacebookToken && (
        <div className={Styles.emptyContainer}>
          <div className={Styles.innerContainer}>
            <Text color="var(--dark-60)" variant="secondary">
              Click to refresh your token with the proper permissions
            </Text>
            <Button variant="secondary" frontIcon={ArrowRefresh2} onPress={refreshToken} fullWidth={false}>
              Refresh Connection
            </Button>
          </div>
        </div>
      )}
      {!isLoading && !hasPosts && !hasInvalidFacebookToken && (
        <div className={Styles.emptyContainer}>
          {(isRefreshing || isRefetching) && (
            <>
              <div className={Styles.loadingOverlay} />
              <DeclarativeToast>
                <ToastMessage toastMessage="Fetching best performing posts..." variant="generating" />
              </DeclarativeToast>
            </>
          )}
          <div className={Styles.innerContainer}>
            <Text color="var(--dark-60)" variant="secondary">
              Click refresh to see your 5 best performing posts here
            </Text>
            <Button variant="secondary" frontIcon={ArrowRefresh2} onPress={refresh} fullWidth={false}>
              Refresh
            </Button>
          </div>
        </div>
      )}
    </Section>
  )
}
